import { useEffect, useRef } from "react";
import { imageUrl } from "../lib/images";

export default function Lightbox({
  images = [],
  currentIndex = 0,
  isOpen = false,
  onClose,
  onNext,
  onPrev,
  captions = [],
}) {
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrev();
    };

    // Lock body scroll
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, onNext, onPrev]);

  if (!isOpen || images.length === 0) return null;

  const currentImg = images[currentIndex];
  const currentCaption = captions[currentIndex] || "";

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current;
      if (diff > 50) {
        onNext(); // swipe left -> next
      } else if (diff < -50) {
        onPrev(); // swipe right -> prev
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col justify-between p-4 sm:p-6 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      onClick={onClose}
    >
      {/* Top bar with count & close button */}
      <div
        className="flex items-center justify-between text-white z-10 w-full max-w-7xl mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold tracking-wider text-neutral-300">
          <span className="text-[#A78E51] font-bold">{currentIndex + 1}</span> / {images.length}
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Luk billedvisning"
          className="p-2.5 rounded-full bg-neutral-800/80 text-white hover:text-[#A78E51] hover:bg-neutral-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#A78E51]"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>
      </div>

      {/* Main image area */}
      <div
        className="relative flex-1 flex items-center justify-center my-2 max-w-6xl mx-auto w-full select-none"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Previous button */}
        {images.length > 1 && (
          <button
            type="button"
            onClick={onPrev}
            aria-label="Forrige billede"
            className="absolute left-2 sm:left-4 z-10 p-3 rounded-full bg-neutral-900/80 text-white hover:text-[#A78E51] hover:bg-neutral-800 transition-all border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-[#A78E51]"
          >
            <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
          </button>
        )}

        {/* The active image */}
        <div className="flex flex-col items-center justify-center max-h-[80vh] w-full">
          <img
            src={imageUrl(currentImg, "width=1600,quality=85,format=auto")}
            alt={currentCaption || "Galleri billede"}
            className="max-h-[75vh] max-w-full object-contain rounded-md shadow-2xl transition-transform duration-200"
          />
          {currentCaption && (
            <p className="mt-3 text-center text-sm text-neutral-300 bg-neutral-900/70 px-4 py-1.5 rounded-full max-w-lg">
              {currentCaption}
            </p>
          )}
        </div>

        {/* Next button */}
        {images.length > 1 && (
          <button
            type="button"
            onClick={onNext}
            aria-label="Næste billede"
            className="absolute right-2 sm:right-4 z-10 p-3 rounded-full bg-neutral-900/80 text-white hover:text-[#A78E51] hover:bg-neutral-800 transition-all border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-[#A78E51]"
          >
            <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Bottom hint */}
      <div
        className="text-center text-xs text-neutral-400 py-1"
        onClick={(e) => e.stopPropagation()}
      >
        <span>Brug piletaster eller swipe for at skifte &bull; Tryk ESC for at lukke</span>
      </div>
    </div>
  );
}
